'use client';

import {
  CountryCombobox,
  type CountryComboboxOption,
} from '@/components/ui/CountryCombobox';
import { Checkbox } from '@/components/ui/checkbox';
import { useLocale, useTranslations } from 'next-intl';
import { useId, useMemo } from 'react';
import {
  EU_COUNTRIES,
  NON_EU_COUNTRIES,
  OTHER_EUROPEAN_COUNTRIES,
  pricingPageVatNote,
  pricingPageVatView,
} from './pricing-helpers';

interface VatControlsProps {
  country: string;
  isBusiness: boolean;
  isVatPayer: boolean;
  onCountryChange: (country: string) => void;
  onBusinessChange: (isBusiness: boolean) => void;
  onVatPayerChange: (isVatPayer: boolean) => void;
  className?: string;
  hideCountry?: boolean;
  /**
   * Suppress the explicit VAT statement rendered under the controls. The note
   * is the *sole* VAT statement on the pricing page (default `false` keeps it),
   * but at checkout the OrderSummary already states the VAT treatment next to
   * the actual total being charged, so the note would duplicate it on the same
   * screen — AddressStep passes `hideNote` to avoid that side-by-side repeat.
   */
  hideNote?: boolean;
  /**
   * Lawyers + firms are *always* businesses, so the "Business" checkbox would
   * be a control that's permanently ticked = noise. In this mode the Business
   * checkbox is hidden and the VAT-payer toggle is always shown (the only
   * meaningful B2B distinction: a VAT-payer sees net + DPH, a non-payer — e.g.
   * a solo advokát below the registration threshold — sees gross). The country
   * selector stays visible for cross-border EU/non-EU firms.
   */
  vatPayerOnly?: boolean;
}

export function VatControls({
  country,
  isBusiness,
  isVatPayer,
  onCountryChange,
  onBusinessChange,
  onVatPayerChange,
  className,
  hideCountry = false,
  hideNote = false,
  vatPayerOnly = false,
}: VatControlsProps) {
  const uid = useId();
  const businessId = `business-${uid}`;
  const vatPayerId = `vatPayer-${uid}`;
  const t = useTranslations('PricingPage');
  const tCommon = useTranslations('Common');
  const locale = useLocale();

  // Create Intl.DisplayNames instance for localized country names
  const languageNames = useMemo(() => {
    return new Intl.DisplayNames([locale], {
      type: 'region',
    });
  }, [locale]);

  // Combine EU countries, named non-EU European countries (Norway, Switzerland,
  // UK, etc.), and the generic "Other" sentinel — all displayed with localized
  // names. OTHER_EUROPEAN_COUNTRIES carries no `vatRate` (it's address-only), but
  // VAT for these resolves through `vatRateForCustomer` → non-EU → 0 % /
  // outside-scope (they're absent from EU_STANDARD_VAT), so no rate is needed
  // here. `XX` stays last as the fallback. Typed to the shape the dropdown reads
  // ({code, name}) so the two helper shapes (with/without vatRate) unify.
  const countryOptions = useMemo<CountryComboboxOption[]>(() => {
    const countries: { code: string; name: string }[] = [
      ...EU_COUNTRIES,
      ...OTHER_EUROPEAN_COUNTRIES,
      ...NON_EU_COUNTRIES,
    ];
    return countries.map((country) => {
      // `XX` ("Other") is the localized sentinel; all real codes display via
      // Intl.DisplayNames (CLDR). `keywords` adds the English name + ISO code so
      // search finds a country by either (e.g. "Switzerland"/"CH" → Švajčiarsko).
      const label =
        country.code === 'XX'
          ? t('VatControls.otherCountries')
          : languageNames.of(country.code) || country.name;

      return {
        value: country.code,
        label,
        keywords: [country.name, country.code],
      };
    });
  }, [languageNames, t]);

  // Explicit VAT statement shown under the controls — but only when it adds
  // information beyond each card's terse per-price suffix. In the gross /
  // VAT-inclusive case the note ("All prices include 23 % VAT.") merely repeats
  // every card's "incl. VAT 23 %", so it is suppressed as redundant. When prices
  // are shown net (domestic VAT-payer → "+ DPH") or zero-rated (reverse-charge /
  // non-EU) the note carries the crucial "VAT is added" / "not applicable"
  // clarification that the suffix alone does not — there it is shown.
  const vatView = pricingPageVatView(country, isBusiness, isVatPayer);
  const vatNote = pricingPageVatNote(country, isBusiness, isVatPayer);
  const vatNoteText = locale === 'sk' ? vatNote.SK : vatNote.EN;
  // Drives the note *content* only; the hideNote DOM gate lives in the JSX below
  // (keeps the aria-live region mounted so changes are announced — see comment).
  const showVatNote = !vatView.includeVat;

  return (
    <div className={`flex flex-col items-center gap-2 ${className ?? 'mb-8'}`}>
      <div className='flex items-center justify-center gap-6 flex-wrap'>
        {!hideCountry && (
          <CountryCombobox
            options={countryOptions}
            value={country}
            onValueChange={onCountryChange}
            placeholder={t('VatControls.selectCountryPlaceholder')}
            searchPlaceholder={tCommon('searchCountry')}
            emptyText={tCommon('noCountryFound')}
            aria-label={t('VatControls.selectCountryPlaceholder')}
            className='w-[120px] bg-surface-card-fancy'
          />
        )}

        {/* Business checkbox — only the Individuals tab needs it (B2C vs B2B).
          Hidden for lawyers/firms, who are always businesses (vatPayerOnly). */}
        {!vatPayerOnly && (
          <div className='flex items-center gap-2'>
            <Checkbox
              id={businessId}
              checked={isBusiness}
              onCheckedChange={(c) => {
                const newValue = Boolean(c);
                onBusinessChange(newValue);
                onVatPayerChange(newValue);
              }}
            />
            <label
              htmlFor={businessId}
              className='text-sm font-medium text-content-secondary cursor-pointer'
            >
              {t('VatControls.businessLabel')}
            </label>
          </div>
        )}

        {/* VAT-payer toggle — shown whenever the customer is a business: gated on
          the Business checkbox for Individuals, always-on for lawyers/firms. */}
        {(vatPayerOnly || isBusiness) && (
          <div className='flex items-center gap-2'>
            <Checkbox
              id={vatPayerId}
              checked={isVatPayer}
              onCheckedChange={(c) => onVatPayerChange(Boolean(c))}
            />
            <label
              htmlFor={vatPayerId}
              className='text-sm font-medium text-content-secondary cursor-pointer'
            >
              {t('VatControls.vatPayerLabel')}
            </label>
          </div>
        )}
      </div>

      {/* hideNote gates DOM presence (checkout opts out — OrderSummary already
          states the VAT treatment). showVatNote gates the *content*: see the
          declaration above for the gross-suppression rationale.
          Accessibility: the aria-live <p> stays mounted whenever !hideNote so
          the live region is registered before any country/business toggle.
          Injecting a fresh aria-live element on change is not reliably announced
          by NVDA+Firefox or VoiceOver+Safari — the region must pre-exist; only
          subsequent text mutations are polled. When suppressed we render empty
          content (no height) rather than unmounting. */}
      {!hideNote && (
        <p
          className='text-xs text-content-secondary text-center'
          aria-live='polite'
        >
          {showVatNote ? vatNoteText : ''}
        </p>
      )}
    </div>
  );
}
